import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { getCatalog } from "../../../api/catalog.service";
import styles from "./ContentPicker.module.sass";

const LEVELS = {
  Newbie: "Principiante",
  Begginer: "Básico",
  Beginner: "Básico",
  Intermediate: "Intermedio",
  Advanced: "Avanzado",
  Master: "Maestro",
};

/** Título del ítem en el idioma pedido, con fallback a inglés y al primero. */
const titleOf = (item, lang = "es") => {
  const list = item?.translations;
  if (!Array.isArray(list) || !list.length) return item?.title || "";
  return (
    list.find((t) => t?.lang === lang)?.trans ||
    list.find((t) => t?.lang === "en")?.trans ||
    list[0]?.trans ||
    ""
  );
};

/** Ítem del catálogo → los datos que necesita una campaña. */
export const toContent = (item, lang = "es") => ({
  id: item?.sourceId || item?._id,
  type: item?.type,
  nombre: titleOf(item, lang),
  n: item?.contentCount ? String(item.contentCount) : "",
  nivel: LEVELS[item?.level] || item?.level || "",
  emoji: item?.emoji || "",
  imageUrl: item?.imageSizes?.mediumUrl || item?.imageUrl || "",
});

/**
 * Buscador de DECK/HISTORIA contra el catálogo. Escribir un ObjectId a mano es
 * la mejor forma de mandar un anuncio a ninguna parte: aquí se busca por nombre
 * y se devuelve el id (más el resto de datos, que las plantillas aprovechan).
 */
const ContentPicker = ({ type = "deck", value, onSelect, lang = "es", disabled }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const label = type === "text" ? "historia" : "deck";

  const search = useCallback(
    async (q) => {
      setLoading(true);
      try {
        // OJO: `page` del catálogo es 0-based (page:1 devuelve la SEGUNDA página,
        // que con take:8 sale vacía casi siempre).
        const res = await getCatalog({ title: q || undefined, type, take: 8, page: 0 });
        const items = res?.data?.results || res?.data?.data || [];
        setResults(Array.isArray(items) ? items : []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [type]
  );

  // Debounce: el catálogo es grande y no hace falta una query por tecla.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => search(query.trim()), query.trim() ? 320 : 0);
    return () => clearTimeout(id);
  }, [query, open, search]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectedLabel = useMemo(() => value?.nombre || "", [value]);

  return (
    <div className={styles.wrap} ref={boxRef}>
      <input
        className={styles.input}
        placeholder={`Busca el ${label} por su nombre…`}
        value={open ? query : selectedLabel || query}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />

      {open && (
        <div className={styles.dropdown}>
          {loading && <div className={styles.empty}>Buscando…</div>}
          {!loading && !results.length && <div className={styles.empty}>Sin resultados</div>}
          {!loading &&
            results.map((item) => {
              const content = toContent(item, lang);
              return (
                <button
                  key={content.id}
                  type="button"
                  className={cn(styles.option, {
                    [styles.optionActive]: value?.id === content.id,
                  })}
                  onClick={() => {
                    onSelect(content);
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  {content.imageUrl ? (
                    <img className={styles.thumb} src={content.imageUrl} alt="" />
                  ) : (
                    <span className={styles.thumbEmoji}>{content.emoji || "🀄️"}</span>
                  )}
                  <span className={styles.optionBody}>
                    <span className={styles.optionName}>{content.nombre || "(sin título)"}</span>
                    <span className={styles.optionMeta}>
                      {[content.nivel, content.n && `${content.n} ítems`]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {value?.id && !open && (
        <div className={styles.selected}>
          <code>{value.id}</code>
        </div>
      )}
    </div>
  );
};

export default ContentPicker;
